import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  standardColours,
  standardTransition,
} from '../styles';
import { Link, Tag, HtmlContent } from './ui';
import { hasStructuredText } from '../utils';

const StyledBlogCard = styled.article`
  border-radius: 20px;
  overflow: hidden;
  padding: 20px;
  background-color: ${standardColours.lightestGrey};
`;

const StyledInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledTop = styled.div`
  padding-bottom: 20px;
`;

const StyledFeaturedImage = styled(GatsbyImage)``;

const StyledTags = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const StyledHeading = styled.h3`
  color: ${brandColours.secondary};
  ${fontSize(22)};
  font-weight: ${fontWeights.semibold};
  margin-top: 20px;
  transition: ${standardTransition('color')};

  &:hover {
    color: ${brandColours.tertiary};
  }
`;
const StyledSummary = styled(HtmlContent)`
  margin-top: 20px;
  p {
    line-height: 1.7;
  }
`;

const StyledText = styled.p`
  margin-top: 20px;
  line-height: 1.7;
`;

const StyledBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding-top: 20px;
  border-top: solid 1px ${brandColours.secondary};
  margin-top: auto;
`;

const StyledImage = styled(GatsbyImage)`
  border-radius: 50%;
  width: 60px;
  height: 60px;
`;

const StyledAuthor = styled.p`
  color: ${brandColours.secondary};
  font-weight: ${fontWeights.semibold};
  transition: ${standardTransition('color')};

  &:hover {
    color: ${brandColours.tertiary};
  }
`;

const BlogCard = blogPost => {
  const {
    featuredImage,
    categories,
    heading,
    summary,
    summaryNew,
    author,
    readTime,
    isFeatured,
  } = blogPost;

  return (
    <StyledBlogCard>
      <StyledInner>
        <StyledTop>
          <Link to={blogPost}>
            <StyledFeaturedImage
              image={featuredImage.gatsbyImageData}
              alt={featuredImage.alt}
            />
          </Link>
          <StyledTags>
            {categories.map((category, id) => (
              <Link key={id} to={category}>
                <Tag>{category.title}</Tag>
              </Link>
            ))}
          </StyledTags>
          <StyledHeading as={isFeatured ? 'h4' : 'h3'}>
            <Link to={blogPost}>{heading}</Link>
          </StyledHeading>
          {hasStructuredText(summaryNew) ? (
            <StyledSummary content={summaryNew} />
          ) : (
            <StyledText>{summary}</StyledText>
          )}
        </StyledTop>
        <StyledBottom>
          <Link to={author}>
            <StyledImage
              image={author.image.gatsbyImageData}
              alt={author.image.alt}
            />
          </Link>
          <div>
            <Link to={author}>
              <StyledAuthor>{author.title}</StyledAuthor>
            </Link>
            {readTime && <p>{readTime}</p>}
          </div>
        </StyledBottom>
      </StyledInner>
    </StyledBlogCard>
  );
};

export default BlogCard;

export const BlogCardFragment = graphql`
  fragment BlogCardFragment on DatoCmsBlog {
    id
    featuredImage {
      gatsbyImageData(width: 770, height: 440)
      alt
    }
    heading: title
    summary
    summaryNew {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    categories {
      title
      ...LinkFragment
    }
    author {
      title
      biography
      image {
        gatsbyImageData(width: 160, height: 160)
        alt
      }
      ...LinkFragment
    }
    readTime
    ...LinkFragment
  }
`;
